import {
  ArrowLeft,
  BookOpen,
  Check,
  Clipboard,
  ExternalLink,
  RefreshCw,
  Save,
  Terminal,
  Wallet,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getSourceAccess } from "../api/client";
import { ZeroCommandCard } from "./ZeroCommandCard";
import type {
  MaterializationDetail,
  MaterializationRequest,
  MaterializationSummary,
  SourceAccessResponse,
} from "../api/types";
import type { WalletSession } from "../hooks/useWalletSession";
import { shortWallet } from "../hooks/useWalletSession";
import { buildZeroVaultListCommand, buildZeroVaultSaveCommand } from "../lib/zeroCommands";

type Props = {
  session: WalletSession | null;
  connecting: boolean;
  loading: boolean;
  saving: boolean;
  readerLoading: boolean;
  error: string | null;
  readerError: string | null;
  materializations: MaterializationSummary[];
  selectedMaterialization: MaterializationDetail | null;
  draftSource: { url: string; title?: string | null } | null;
  onConnect: () => void;
  onClose: () => void;
  onRefresh: () => void;
  onSave: (request: MaterializationRequest) => Promise<unknown>;
  onOpenMaterialization: (id: string) => void;
  onCloseMaterialization: () => void;
};

const fieldClass =
  "w-full rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/80 outline-none transition placeholder:text-white/25 focus:border-flame-400/50 focus:bg-white/[0.06]";

const labelClass =
  "font-mono text-[10px] uppercase tracking-[0.18em] text-white/40";

const shortHash = (hash: string): string => `${hash.slice(0, 8)}…${hash.slice(-6)}`;

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

export const VaultPanel = ({
  session,
  connecting,
  loading,
  saving,
  readerLoading,
  error,
  readerError,
  materializations,
  selectedMaterialization,
  draftSource,
  onConnect,
  onClose,
  onRefresh,
  onSave,
  onOpenMaterialization,
  onCloseMaterialization,
}: Props) => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [runId, setRunId] = useState("");
  const [licenseStatus, setLicenseStatus] =
    useState<NonNullable<MaterializationRequest["license_status"]>>("unknown");
  const [markdown, setMarkdown] = useState("");
  const [savedId, setSavedId] = useState<string | null>(null);
  const [accessPlan, setAccessPlan] = useState<SourceAccessResponse | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const vaultListCommand = useMemo(() => buildZeroVaultListCommand(), []);
  const vaultSaveCommand = useMemo(() => buildZeroVaultSaveCommand(), []);

  useEffect(() => {
    if (!draftSource) return;
    setUrl(draftSource.url);
    setTitle(draftSource.title ?? "");
  }, [draftSource]);

  const canSave = useMemo(
    () => Boolean(session && url.trim() && markdown.trim() && !saving),
    [markdown, saving, session, url],
  );

  const accessMethod = accessPlan?.methods[0] ?? null;
  const readerContent =
    selectedMaterialization?.markdown ??
    selectedMaterialization?.text_content ??
    (selectedMaterialization
      ? JSON.stringify(selectedMaterialization.raw_json, null, 2)
      : "");

  const loadAccessPlan = async () => {
    if (!url.trim()) return;
    setAccessLoading(true);
    setAccessError(null);
    setCopied(false);
    try {
      setAccessPlan(await getSourceAccess(url.trim()));
    } catch (err) {
      setAccessPlan(null);
      setAccessError(err instanceof Error ? err.message : "source access failed");
    } finally {
      setAccessLoading(false);
    }
  };

  const copyCommand = async () => {
    if (!accessMethod?.command) return;
    await navigator.clipboard.writeText(accessMethod.command);
    setCopied(true);
  };

  const handleSave = async () => {
    if (!canSave) return;
    const saved = await onSave({
      url: url.trim(),
      title: title.trim() || undefined,
      provider: "zero",
      provider_run_id: runId.trim() || undefined,
      content_type: "markdown",
      markdown,
      license_status: licenseStatus,
      cache_policy: "private_wallet_cache",
      allowed_outputs: {
        public_search: "metadata_only",
        wallet_search: "full_text",
      },
      metadata: { entrypoint: "web_vault" },
    });
    if (saved && typeof saved === "object" && "id" in saved) {
      setSavedId(String(saved.id));
      setMarkdown("");
      setRunId("");
    }
  };

  return (
    <aside className="panel rounded-2xl shadow-terminal overflow-hidden flex flex-col max-h-full">
      <div className="p-5 pb-4 flex items-start gap-3 border-b border-white/5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="chip">wallet vault</span>
            {session ? (
              <span className="chip chip-muted">{shortWallet(session.wallet)}</span>
            ) : (
              <span className="chip chip-muted">disconnected</span>
            )}
          </div>
          <h2 className="font-display text-xl font-bold text-white tracking-tight leading-snug">
            Source materializations
          </h2>
        </div>
        <button
          onClick={onClose}
          className="text-white/40 hover:text-white w-7 h-7 rounded-md hover:bg-white/5 flex items-center justify-center"
          aria-label="Close vault"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-5 pb-5 pt-4 overflow-y-auto panel-scroll">
        {!session && (
          <div className="space-y-3">
            <ZeroCommandCard command={vaultListCommand} compact />
            <ZeroCommandCard command={vaultSaveCommand} compact />
            <button
              onClick={onConnect}
              disabled={connecting}
              className="btn-ghost w-full rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2"
            >
              <Wallet className="h-4 w-4" />
              {connecting ? "connecting" : "browser fallback"}
            </button>
          </div>
        )}

        {session && selectedMaterialization && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onCloseMaterialization}
                className="btn-ghost rounded-md px-2 py-1.5 text-xs flex items-center gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                back
              </button>
              <span className="chip chip-muted">
                {selectedMaterialization.content_type}
              </span>
            </div>
            <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white/90 truncate">
                    {selectedMaterialization.title ??
                      selectedMaterialization.canonical_url ??
                      selectedMaterialization.id}
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-white/35 truncate">
                    {selectedMaterialization.provider}
                    {selectedMaterialization.provider_run_id
                      ? ` · ${selectedMaterialization.provider_run_id}`
                      : ""}
                  </div>
                </div>
                <span className="chip chip-muted flex-shrink-0">
                  {selectedMaterialization.license_status}
                </span>
              </div>
              {selectedMaterialization.canonical_url && (
                <a
                  href={selectedMaterialization.canonical_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 btn-ghost rounded-md px-3 py-2 text-xs flex items-center justify-center gap-2"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  open source
                </a>
              )}
            </div>
            <pre className="panel-scroll max-h-[360px] whitespace-pre-wrap overflow-auto rounded-md border border-white/10 bg-black/35 p-3 text-xs leading-relaxed text-white/75">
              {readerContent || "(empty materialization)"}
            </pre>
          </div>
        )}

        {session && !selectedMaterialization && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <label className="space-y-1.5">
                <span className={labelClass}>source url</span>
                <div className="flex gap-2">
                  <input
                    value={url}
                    onChange={(event) => {
                      setUrl(event.target.value);
                      setAccessPlan(null);
                      setAccessError(null);
                      setCopied(false);
                    }}
                    className={fieldClass}
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={loadAccessPlan}
                    disabled={!url.trim() || accessLoading}
                    className="btn-ghost rounded-md px-3 text-xs flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Terminal className="h-3.5 w-3.5" />
                    zero
                  </button>
                </div>
              </label>

              {(accessMethod || accessError) && (
                <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
                  {accessMethod?.command && (
                    <>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={labelClass}>{accessMethod.label}</span>
                          {accessMethod.price_usdc_micro !== null && (
                            <span className="chip chip-muted">
                              ${(accessMethod.price_usdc_micro / 1_000_000).toFixed(4)}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={copyCommand}
                          className="chip hover:opacity-80"
                        >
                          <Clipboard className="h-3 w-3" />
                          {copied ? "copied" : "copy"}
                        </button>
                      </div>
                      <pre className="panel-scroll max-h-[96px] overflow-auto rounded bg-black/30 p-2 font-mono text-[10px] leading-relaxed text-white/60">
                        {accessMethod.command}
                      </pre>
                    </>
                  )}
                  {accessError && (
                    <div className="font-mono text-xs text-red-200">! {accessError}</div>
                  )}
                </div>
              )}

              <label className="space-y-1.5">
                <span className={labelClass}>title</span>
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  className={fieldClass}
                  placeholder="optional"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1.5">
                  <span className={labelClass}>zero run</span>
                  <input
                    value={runId}
                    onChange={(event) => setRunId(event.target.value)}
                    className={fieldClass}
                    placeholder="optional"
                  />
                </label>
                <label className="space-y-1.5">
                  <span className={labelClass}>license</span>
                  <select
                    value={licenseStatus}
                    onChange={(event) =>
                      setLicenseStatus(
                        event.target
                          .value as NonNullable<MaterializationRequest["license_status"]>,
                      )
                    }
                    className={fieldClass}
                  >
                    <option value="unknown">unknown</option>
                    <option value="copyrighted">copyrighted</option>
                    <option value="publisher_license">publisher license</option>
                    <option value="permissive">permissive</option>
                    <option value="public_domain">public domain</option>
                    <option value="user_provided">user provided</option>
                  </select>
                </label>
              </div>
              <label className="space-y-1.5">
                <span className={labelClass}>markdown</span>
                <textarea
                  value={markdown}
                  onChange={(event) => setMarkdown(event.target.value)}
                  className={`${fieldClass} min-h-[150px] resize-none leading-relaxed`}
                  placeholder="# fetched source"
                />
              </label>
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="btn-primary rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? "saving" : "save to vault"}
              </button>
              {url.trim() && (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost rounded-md px-3 py-2 text-xs flex items-center justify-center gap-2"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  open source
                </a>
              )}
            </div>

            {savedId && (
              <div className="flex items-center gap-2 rounded-md border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-200">
                <Check className="h-4 w-4" />
                saved {savedId.slice(0, 8)}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-md border border-red-400/25 bg-red-400/10 px-3 py-2 font-mono text-xs text-red-200">
            ! {error}
          </div>
        )}

        {readerError && (
          <div className="mt-4 rounded-md border border-red-400/25 bg-red-400/10 px-3 py-2 font-mono text-xs text-red-200">
            ! {readerError}
          </div>
        )}

        <div className="mt-5 border-t border-white/5 pt-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className={labelClass}>
              materialized sources {materializations.length}
            </div>
            {session && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="btn-ghost rounded-md px-2 py-1.5 text-xs flex items-center gap-1.5"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                />
                refresh
              </button>
            )}
          </div>

          {materializations.length === 0 ? (
            <div className="rounded-md border border-white/5 bg-white/[0.03] px-3 py-4 text-sm text-white/45">
              no private sources
            </div>
          ) : (
            <ul className="space-y-0 divide-y divide-white/5">
              {materializations.map((item) => (
                <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    onClick={() => onOpenMaterialization(item.id)}
                    disabled={readerLoading}
                    className="w-full rounded-md p-2 -mx-2 text-left hover:bg-white/[0.04] transition disabled:opacity-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm text-white/85">
                          <BookOpen className="h-3.5 w-3.5 text-flame-400/70 flex-shrink-0" />
                          <span className="truncate">
                            {item.title ?? item.canonical_url ?? item.source_id}
                          </span>
                        </div>
                        <div className="font-mono text-[10px] text-white/35 mt-1 truncate">
                          {item.provider}
                          {item.provider_run_id ? ` · ${item.provider_run_id}` : ""}
                        </div>
                      </div>
                      <span className="chip chip-muted flex-shrink-0">
                        {item.license_status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between font-mono text-[10px] text-white/30">
                      <span>{shortHash(item.content_sha256)}</span>
                      <span>{formatDate(item.fetched_at)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
};
